import React, { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IMediaItem } from "@/lib/community";
import MediaLightbox from "./MediaLightBox";

interface MediaSliderProps {
  media: IMediaItem[];
  size?: number;
  blocks?: number;
}

export default function MediaSlider({
  media,
  size,
  blocks = 4,
}: MediaSliderProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    align: "start",
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollButtons = () => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  };

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", updateScrollButtons);
    updateScrollButtons();
  }, [emblaApi]);

  const blockSize = size ? `${size}rem` : "10rem";

  const videoBlockSize = {
    height: blockSize,
    width: `${(16 / 9) * size}rem`,
  };

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {media.map((item, idx) => (
            <div key={idx} className={` flex-none rounded-lg overflow-hidden`}>
              <MediaLightbox
                content={
                  item.type === "image" ? (
                    <img
                      src={item.url}
                      className="w-full h-full object-contain"
                    />
                  ) : item.url.includes("embed") ? (
                    <iframe
                      src={item.url}
                      className="w-full aspect-video"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={item.url}
                      controls
                      className="w-full max-h-[80vh] object-contain"
                    />
                  )
                }
              >
                {item.type === "image" ? (
                  <img
                    src={item.url}
                    alt="media"
                    className={`w-full object-cover`}
                    style={{
                      height: blockSize,
                      width: blockSize,
                    }}
                  />
                ) : item.url.includes("embed") ? (
                  <iframe
                    src={item.url}
                    className="w-full aspect-video"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    style={{
                      height: videoBlockSize.height,
                      width: videoBlockSize.width,
                    }}
                  />
                ) : (
                  <video
                    src={item.url}
                    className={`w-full object-cover`}
                    style={{
                      height: videoBlockSize.height,
                      width: videoBlockSize.width,
                    }}
                    muted
                  />
                )}
              </MediaLightbox>
            </div>
          ))}
        </div>
      </div>

      {canScrollPrev && (
        <button
          onClick={() => emblaApi?.scrollPrev()}
          className="absolute left-0 top-1/2 -translate-y-1/2 bg-white p-1 rounded-full shadow hover:bg-gray-100 dark:bg-black"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canScrollNext && (
        <button
          onClick={() => emblaApi?.scrollNext()}
          className="absolute right-0 top-1/2 -translate-y-1/2 bg-white p-1 rounded-full shadow hover:bg-gray-100 dark:bg-black"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
